import { useCallback } from 'react'

import { isEqual } from 'date-fns'
import { useTheme } from 'styled-components'

import { Loading } from '@components/Loading'
import { Separator } from '@components/Separator'
import { MidstreamSwipe } from '@containers/Task/Actions/MidstreamSwipe'
import { LoaderContainer, NoTasksText } from '@containers/Task/Lists/styles'
import { MidstreamTile } from '@containers/Task/Tiles/MidstreamTile'
import { useSearch } from '@contexts/search'
import { useSharedDates } from '@contexts/sharedDates'
import { useDate } from '@hooks/dates'
import { useFindTasks } from '@hooks/tasks/find'

export function SearchMidstreamTasks() {
  const { searchTerm } = useSearch()
  const theme = useTheme()

  const { getWeekRange, getSiteStartTime } = useDate()
  const { selectedDate, selectedWeekDay } = useSharedDates()
  const { weekStart, weekEnd } = getWeekRange(selectedDate)
  const todoStartTime = getSiteStartTime(5, selectedWeekDay.date)

  const { data: rawTasks, isLoading } = useFindTasks({
    startDate: weekStart,
    endDate: weekEnd,
    view: 'midstream',
  })

  const tasks = rawTasks ?? []
  type TTask = (typeof tasks)[number]

  const searchResults = useCallback(
    (task: TTask) => {
      if (!searchTerm) return true

      const lowerCaseSearchTerm = searchTerm.toLowerCase()

      return (
        task.code?.toLowerCase().includes(lowerCaseSearchTerm) ||
        task.jobName?.toLowerCase().includes(lowerCaseSearchTerm) ||
        (typeof task.location === 'object' &&
          task.location?.description
            .toLowerCase()
            .includes(lowerCaseSearchTerm)) ||
        task.fls?.description?.toLowerCase().includes(lowerCaseSearchTerm)
      )
    },
    [searchTerm],
  )

  const timeResults = useCallback(
    ({ startTime, status }: TTask) =>
      !isEqual(new Date(startTime), todoStartTime) && status !== 'imported',
    [todoStartTime],
  )

  const tasksResult = tasks
    .filter(timeResults)
    .filter(searchResults)
    .sort(
      (a, b) =>
        new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
    )

  const renderItem = useCallback(
    (task: TTask) => (
      <MidstreamSwipe task={task}>
        <MidstreamTile task={task} />
      </MidstreamSwipe>
    ),
    [],
  )

  return (
    <>
      {isLoading ? (
        <LoaderContainer>
          <Loading color={theme.colors.text} size="small" />
        </LoaderContainer>
      ) : tasksResult.length > 0 && searchTerm ? (
        <div>
          {tasksResult.map((task, index) => (
            <div key={task.id.toString()}>
              {index > 0 && <Separator />}
              {renderItem(task)}
            </div>
          ))}
        </div>
      ) : (
        <LoaderContainer>
          <NoTasksText>Oops, no tasks found.</NoTasksText>
        </LoaderContainer>
      )}
    </>
  )
}
